import React, { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { TopNavigationProps, TopNavigationModel } from './TopNavigation.types';
import { StyledTopNavigation } from './TopNavigation.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import { Button, Icon, Link, LogoContainer } from '@marriott/mi-ui-library';
import { getDirection, useDCADynamicModelAlloy, debounce } from '../../modules/helpers';
import { LanguageSelector } from '../LanguageSelector';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

export const TopNavigation: FC<TopNavigationProps> = (props: TopNavigationProps) => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;
  const isLogoFlagEnabled = props.featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const model: TopNavigationModel = targetData ? targetData : props?.model;
  const NavBarRef = useRef<HTMLDivElement>(null);
  const langSelectorRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false); // Add state for button toggle
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false); // Add state for button toggle

  const mainNavRef = useRef(null) as unknown as MutableRefObject<HTMLDivElement | null>; //Reference to <nav> element ('.main-nav__nav')

  const mainNavOverlayRef = useRef(null) as unknown as MutableRefObject<HTMLDivElement | null>; //Reference to .main-nav__overlay
  const mainNavMenuButtonRef = useRef(null) as unknown as MutableRefObject<HTMLDivElement | null>; //Reference to .main-nav__menu-btn

  const STR_OPEN_MENU = 'Open menu';
  const STR_CLOSE_MENU = 'Close menu';

  /**
   * Memoizing the function to ensure not to re-create b/w the subsequent renders.
   */
  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen(prevState => !prevState);
  }, []);

  const toggleHandler = () => {
    setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClickOnSelectorButton = (event: any) => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      toggleHandler();
    }
  };

  const [viewPort, setViewPort] = useState('Desktop');
  const [direction, setDirection] = useState(false);

  /**
   * Last Scroll postion Value
   */
  const lastScrollPosition = useRef<number>(0);

  const handleScroll = useCallback(() => {
    /**
     * window scroll across Y axis
     */
    const currentScrollPosition = window.scrollY;
    const navBarMain = NavBarRef?.current;

    //get's the navBar height
    const topnavHeight = navBarMain?.getBoundingClientRect()?.height;

    /**
     * add the sticky bottom if scrolled bottom
     * when the current scroll postion is greater than the nav bar height
     */
    if (topnavHeight && currentScrollPosition > topnavHeight && currentScrollPosition > lastScrollPosition.current) {
      if (navBarMain) {
        navBarMain?.classList.add('sticky-nav-bar-bottom');
        navBarMain?.classList.remove('sticky-nav-bar-top');
        navBarMain?.classList.remove('nav-bar-overlay');
      }
    } else if (
      currentScrollPosition < lastScrollPosition.current &&
      topnavHeight &&
      currentScrollPosition > topnavHeight
    ) {
      /**
       * add the sticky top class if scrolled top
       * when the current scroll postion is greater than the nav bar height
       */
      if (navBarMain) {
        navBarMain?.classList.add('sticky-nav-bar-top');
        navBarMain?.classList.remove('sticky-nav-bar-bottom');
        navBarMain?.classList.remove('nav-bar-overlay');
      }
    } else {
      /**
       * remove the sticky classes if NavBar is back to top
       */
      navBarMain?.classList.remove('sticky-nav-bar-top');
      navBarMain?.classList.remove('sticky-nav-bar-bottom');
      navBarMain?.classList.add('nav-bar-overlay');
    }
    /**
     * Assigning the last Scroll Position
     */
    lastScrollPosition.current = currentScrollPosition;
  }, []);

  useEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);

    window.addEventListener('load', handleResize);
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleResize = useCallback(() => {
    const xs = window.matchMedia('(max-width: 767px)');
    const md = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
    const lg = window.matchMedia('(min-width: 1200px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else if (md?.matches) {
      setViewPort('Tablet');
    } else if (lg?.matches) {
      setViewPort('Desktop');
    }
  }, []);

  const changeNavOverFlowStyle = (overflow: 'hidden' | 'auto' | 'visible' | '') => {
    if (!mainNavRef?.current) {
      return;
    }
    mainNavRef.current.style.overflow = overflow;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const menuNav = mainNavRef?.current;
      const menuButton = mainNavMenuButtonRef?.current;
      const overlay = mainNavOverlayRef?.current;
      const langSelector = langSelectorRef?.current;

      if ((!menuNav?.contains(target) && !menuButton?.contains(target) && !langSelector) || overlay?.contains(target)) {
        setIsMenuOpen(false);
      }
    };
    if (viewPort !== 'Desktop') {
      changeNavOverFlowStyle('hidden');

      if (isMenuOpen) {
        document.addEventListener('click', handleClickOutside, true);
        document.body.style.overflow = 'hidden'; // Prevent scrolling when the menu is open
      } else {
        document.removeEventListener('click', handleClickOutside);
        document.body.style.overflow = 'auto'; // Restore scrolling when the menu is closed
      }
    }
    setDirection(getDirection());
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'auto'; // Restore scrolling on unmount as well
      changeNavOverFlowStyle('');
    };
  }, [isMenuOpen]);

  const navItems = model?.headerLinks.map((navItem, index) => {
    return (
      <li className="main-nav__nav-item" key={index} role="presentation">
        <Link
          text={navItem.ctaProperties.linkText}
          linkClassName={'m-nav-link t-label-s custom_click_track'}
          ariaLabel={navItem.ctaProperties.linkText}
          linkHref={navItem.ctaProperties.linkUrl}
          linkType="internal"
          target={navItem.ctaProperties.openInANewTab ? '_blank' : '_self'}
          custom_click_track_value={navItem.clickTrackValue}
        >
          <span
            className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right', 'main-nav__nav-link-arrow d-lg-none')}
            data-testid="menu-right-arrow"
          ></span>
        </Link>
      </li>
    );
  });

  return (
    <StyledTopNavigation data-testid="luxury-TopNavigation" data-component-name="m-luxury-TopNavigation">
      <div
        ref={NavBarRef}
        className={clsx(
          !model?.styleclass?.includes('dark-theme') ? 'color-scheme1' : 'nav-bar-overlay',
          'm-container-fullbleed'
        )}
      >
        <div
          className={clsx(`main-nav__content ${model?.styleclass}`, {
            'main-nav__open ': isMenuOpen,
          })}
          data-testid="primarynavigation"
        >
          <div className="logo-wrapper">
            <Button
              id="menuButton"
              className={clsx(`main-nav__btn main-nav__menu-btn d-lg-none main-nav__menu-btn--close`, {
                'hamburger-menu': !isMenuOpen,
              })}
              ariaExpanded={isMenuOpen}
              callback={handleMenuToggle}
              ariaLabel={isMenuOpen ? STR_CLOSE_MENU : STR_OPEN_MENU}
              ref={mainNavMenuButtonRef}
            >
              <Icon
                iconClass={`icon-l ${
                  isMenuOpen ? 'main-nav__menu-close-icon icon-clear' : 'main-nav__menu-open-icon icon-menu'
                }`}
              />
            </Button>

            <div className="luxury-logo">
              {!isLogoFlagEnabled ? (
                <Link
                  linkClassName=""
                  linkHref={model?.linkUrl as string}
                  custom_click_track_value=""
                  ariaLabel={model?.logo}
                  target="_self"
                >
                  <span className="sr-only">{model?.logo}</span>
                  <span className={model?.logo} />
                </Link>
              ) : (
                <LogoContainer
                  brand={model?.logo || ''}
                  color={'background-aware'}
                  aspectRatio={'3:2'}
                  width={60}
                  height={40}
                  alignment={'Centered'}
                  isLink={true}
                  linkUrl={model?.linkUrl}
                  hoverColor={''}
                  brandFullName={model?.logo}
                />
              )}
            </div>
          </div>
          <div>
            <nav
              ref={mainNavRef}
              className={clsx('nav-container main-nav__nav', { 'd-flex position-static': isMenuOpen })}
            >
              {isMenuOpen && <div className="main-nav__overlay" ref={mainNavOverlayRef}></div>}
              <ul className={'main-nav__nav-items main-nav'}>{navItems}</ul>
            </nav>
          </div>
          {model?.showLanguageSelector && props?.languageSelectorModal && (
            <div
              className={clsx('language-selector-button')}
              tabIndex={0}
              onKeyDown={handleClickOnSelectorButton}
              role="button"
            >
              <Button
                isLink={true}
                className={clsx(
                  'language-selector-button t-font-xs custom_click_track',
                  isMenuOpen ? 'd-flex' : 'd-none'
                )}
                callback={toggleHandler}
                ariaLabel={model?.currentLanguage}
                custom_click_track_value={model?.clickTrackValue}
                ariaRole="button"
              >
                <Icon iconClass={clsx('icon-globe icon-s')} />
                <span aria-hidden="true" className="pl-1">
                  {model?.currentLanguage}
                </span>
              </Button>
            </div>
          )}
        </div>
        {model?.showLanguageSelector && props?.languageSelectorModal && (
          <div className="language-selector-button">
            <LanguageSelector
              langSelectorRef={langSelectorRef}
              currentLanguage={model?.currentLanguage || ''}
              clickTrackValue={model?.clickTrackValue}
              toggleHandler={toggleHandler}
              setIsOpen={setIsLanguageSelectorOpen}
              isOpen={isLanguageSelectorOpen}
              languageSelectorModal={props?.languageSelectorModal}
            />
          </div>
        )}
      </div>
    </StyledTopNavigation>
  );
};

export const TopNavigationConfig = {
  emptyLabel: 'TopNavigation',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/top-navigation/v1/top-navigation`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TopNavigationEditable = (props: any) => (
  <div data-testid="topNavigation" data-component-name="m-topNavigation">
    <EditableComponent config={TopNavigationConfig} {...props}>
      <TopNavigation {...props} />
    </EditableComponent>
  </div>
);
